/**
 * apiClient.js
 *
 * Centralized Axios instance for all API calls.
 *
 * - baseURL: REACT_APP_API_URL ?? '/api'
 * - 401  → attempts token refresh once, then redirects to /login
 * - 403  → toast "forbidden"
 * - 500+ → toast "server error"
 *
 * AuthContext (AuthProvider effect) attaches the request interceptor
 * (Authorization header) and the 401 refresh -> retry -> /login redirect logic
 * to BOTH this instance and the global `axios` default instance (the latter
 * covers the many call sites that use raw `axios.*` instead of apiClient).
 * This keeps the axios instance importable before React mounts, while the
 * actual auth wiring happens inside the AuthProvider effect.
 */

import axios from 'axios';

const BASE_URL =
  (process.env.REACT_APP_API_URL ?? process.env.REACT_APP_BACKEND_URL)
    ? `${process.env.REACT_APP_API_URL ?? process.env.REACT_APP_BACKEND_URL}/api`
    : '/api';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// ── Toast helper ─────────────────────────────────────────────────────────────
// We keep a module-level setter so the ToastContext can register itself without
// a circular import.  Before registration, errors are silently logged.
let _showToast = null;

export function registerToastHandler(fn) {
  _showToast = fn;
}

function fireToast(type, msg) {
  if (_showToast) {
    _showToast(type, msg);
  } else {
    console.warn('[apiClient toast]', type, msg);
  }
}

// ── Error interceptor (non-auth errors) ──────────────────────────────────────
// Auth-specific 401 handling is added by AuthContext via setupApiClientInterceptors.
// This global interceptor only fires for non-retry paths.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 403 — forbidden
    if (status === 403 && !error.config?._silentForbidden) {
      const msg =
        error.response?.data?.message_tr ||
        error.response?.data?.message ||
        'Bu işlem için yetkiniz yok.';
      fireToast('error', msg);
    }

    // 5xx — server errors
    if (status >= 500) {
      const msg =
        error.response?.data?.message_tr ||
        error.response?.data?.message ||
        'Sunucu hatası oluştu. Lütfen tekrar deneyin.';
      fireToast('error', msg);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
